import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import UserStatusIndicator from '../../components/ui/UserStatusIndicator';
import CallStatusWidget from '../../components/ui/CallStatusWidget';
import AgentStatusGrid from './components/AgentStatusGrid';
import PerformanceMetrics from './components/PerformanceMetrics';
import CallQueueManager from './components/CallQueueManager';
import RealTimeAlerts from './components/RealTimeAlerts';
import Icon from '../../components/AppIcon';

const SupervisorMonitor = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Auto-refresh functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(new Date());
      // Trigger data refresh for all components
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  const tabs = [
    {
      id: 'overview',
      label: 'Genel Bakış',
      icon: 'LayoutDashboard',
      description: 'Tüm metriklerin özeti'
    },
    {
      id: 'agents',
      label: 'Temsilciler',
      icon: 'Users',
      description: 'Temsilci durumu ve performans'
    },
    {
      id: 'queues',
      label: 'Kuyruklar',
      icon: 'List',
      description: 'Kuyruk yönetimi ve bekleyen aramalar'
    },
    {
      id: 'alerts',
      label: 'Uyarılar',
      icon: 'Bell',
      description: 'Gerçek zamanlı sistem uyarıları'
    }
  ];

  const handleManualRefresh = () => {
    setLastRefresh(new Date());
    // Trigger manual refresh for all components
  };

  const formatLastRefresh = (date) => {
    return date?.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <NavigationBar />
      <UserStatusIndicator />
      <CallStatusWidget />
      <div className="content-offset px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Süpervizör İzleme</h1>
                <p className="text-slate-600 mt-2">
                  Çağrı merkezi performansını gerçek zamanlı izleyin ve yönetin
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-slate-600">Son Güncelleme</p>
                  <p className="text-sm font-medium text-slate-900">
                    {formatLastRefresh(lastRefresh)}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(parseInt(e?.target?.value))}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10 saniye</option>
                    <option value={30}>30 saniye</option>
                    <option value={60}>1 dakika</option>
                    <option value={300}>5 dakika</option>
                  </select>
                  
                  <button
                    onClick={handleManualRefresh}
                    className="p-2 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    title="Manuel Yenile"
                  >
                    <Icon name="RefreshCw" size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-slate-200">
              <nav className="-mb-px flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={16} 
                      className={`mr-2 ${
                        activeTab === tab?.id ? 'text-blue-500' : 'text-slate-400 group-hover:text-slate-500'
                      }`}
                    />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <PerformanceMetrics />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  <AgentStatusGrid />
                  <CallQueueManager />
                </div>
              </div>
            )}

            {activeTab === 'agents' && (
              <AgentStatusGrid />
            )}

            {activeTab === 'queues' && (
              <CallQueueManager />
            )}

            {activeTab === 'alerts' && (
              <RealTimeAlerts />
            )}
          </div>

          {/* Quick Actions Panel */}
          <div className="fixed bottom-4 left-4 bg-white rounded-lg shadow-lg border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-900 mb-3">Hızlı İşlemler</h4>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center">
                <Icon name="Broadcast" size={14} className="mr-2 text-slate-400" />
                Tüm Temsilcilere Duyuru
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center">
                <Icon name="UserPlus" size={14} className="mr-2 text-slate-400" />
                Acil Durum Temsilcisi Çağır
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center">
                <Icon name="Download" size={14} className="mr-2 text-slate-400" />
                Rapor İndir
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center">
                <Icon name="Settings" size={14} className="mr-2 text-slate-400" />
                Sistem Ayarları
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisorMonitor;